<script lang="ts">
	import { onMount, onDestroy, tick } from 'svelte'
	import type {
		ContextMenuActionItem,
		ContextMenuItem,
		ContextMenuProps
	} from '@repo/commons/models'
	import { portal } from 'svelte-portal'
	import { scaleFade } from '@repo/commons/utils'
	import { Icon } from '@repo/commons/components'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'

	const DEFAULT_CONTEXT_MENU_PORTAL_ID = 'context-menu-portal-root'
	const getPortalRoot = (id: string): HTMLElement | null =>
		typeof document === 'undefined' ? null : document.getElementById(id)

	let {
		x = 0,
		y = 0,
		items = [],
		onClose = () => {},
		portalParent = null
	}: ContextMenuProps = $props()
	let adjustedX: number = $state(x)
	let adjustedY: number = $state(y)
	let justOpened: boolean = true
	let menuDivElement: HTMLElement
	const resolvedPortalParent = $derived.by<HTMLElement | 'body'>(
		() => portalParent ?? getPortalRoot(DEFAULT_CONTEXT_MENU_PORTAL_ID) ?? 'body'
	)

	let activeSubMenuIndex: number = $state(-1)
	let subMenuAnchorRect: DOMRect | null = $state(null)
	let adjustedSubMenuX: number = $state(0)
	let adjustedSubMenuY: number = $state(0)
	let subMenuDivElement: HTMLElement | undefined = $state()
	let closeSubMenuTimer: ReturnType<typeof setTimeout> | null = null

	const SUB_MENU_OFFSET = 4
	const VIEWPORT_PADDING = 10

	const activeSubMenuItems = $derived.by((): ContextMenuItem[] => {
		if (activeSubMenuIndex < 0) return []
		const item = items[activeSubMenuIndex]
		if (!item || item.separator) return []
		return item.subItems ?? []
	})

	const handleClick = (event: MouseEvent, callback: () => void, disabled: boolean) => {
		event.preventDefault()
		event.stopPropagation()
		if (disabled) return
		callback()
		// Defer close to the next frame to avoid click-through in optimized builds.
		requestAnimationFrame(() => onClose())
	}

	const handleOutsideClick = (event: MouseEvent | PointerEvent) => {
		if (justOpened) return

		const eventPath = event.composedPath()
		const clickedInsideMainMenu = !!menuDivElement && eventPath.includes(menuDivElement)
		const clickedInsideSubMenu = !!subMenuDivElement && eventPath.includes(subMenuDivElement)
		if (!clickedInsideMainMenu && !clickedInsideSubMenu) {
			onClose()
		}
	}

	async function adjustMenuPosition() {
		await tick()
		if (!menuDivElement) return

		const rect = menuDivElement.getBoundingClientRect()

		adjustedX =
			rect.right > window.innerWidth ? Math.max(10, window.innerWidth - rect.width - 10) : x

		adjustedY =
			rect.bottom > window.innerHeight ? Math.max(10, window.innerHeight - rect.height - 10) : y
	}

	function adjustSubMenuPosition() {
		if (!subMenuAnchorRect || !subMenuDivElement || !menuDivElement) return

		const menuRect = menuDivElement.getBoundingClientRect()
		const subMenuWidth = subMenuDivElement.offsetWidth
		const subMenuHeight = subMenuDivElement.offsetHeight
		const preferredRightX = menuRect.right + SUB_MENU_OFFSET
		const preferredLeftX = menuRect.left - subMenuWidth - SUB_MENU_OFFSET
		const fitsOnRight = preferredRightX + subMenuWidth <= window.innerWidth - VIEWPORT_PADDING
		const fitsOnLeft = preferredLeftX >= VIEWPORT_PADDING

		adjustedSubMenuX = fitsOnRight
			? preferredRightX
			: fitsOnLeft
				? preferredLeftX
				: Math.max(VIEWPORT_PADDING, window.innerWidth - subMenuWidth - VIEWPORT_PADDING)

		adjustedSubMenuY = Math.min(
			Math.max(VIEWPORT_PADDING, subMenuAnchorRect.top),
			Math.max(VIEWPORT_PADDING, window.innerHeight - subMenuHeight - VIEWPORT_PADDING)
		)
	}

	$effect(() => {
		// These references are needed to trigger this effect when they change
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const _ = [x, y, menuDivElement]
		adjustMenuPosition()
	})

	$effect(() => {
		// These references are needed to trigger this effect when they change
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const _ = [subMenuAnchorRect, subMenuDivElement, activeSubMenuItems.length]
		adjustSubMenuPosition()
	})

	const scheduleCloseSubMenu = () => {
		closeSubMenuTimer = setTimeout(() => {
			activeSubMenuIndex = -1
			subMenuAnchorRect = null
		}, 120)
	}

	const cancelCloseSubMenu = () => {
		if (closeSubMenuTimer) {
			clearTimeout(closeSubMenuTimer)
			closeSubMenuTimer = null
		}
	}

	const handleItemMouseEnter = (index: number, element: HTMLElement) => {
		cancelCloseSubMenu()
		const item = items[index]
		if (item.separator) {
			activeSubMenuIndex = -1
			subMenuAnchorRect = null
			return
		}
		if (item.subItems?.length) {
			const anchorRect = element.getBoundingClientRect()
			activeSubMenuIndex = index
			subMenuAnchorRect = anchorRect
			adjustedSubMenuX = menuDivElement.getBoundingClientRect().right + SUB_MENU_OFFSET
			adjustedSubMenuY = anchorRect.top
		} else {
			activeSubMenuIndex = -1
			subMenuAnchorRect = null
		}
	}

	const handleItemMouseLeave = () => {
		scheduleCloseSubMenu()
	}

	const handleSubMenuMouseEnter = () => {
		cancelCloseSubMenu()
	}

	const handleSubMenuMouseLeave = () => {
		scheduleCloseSubMenu()
	}

	onMount(async () => {
		await tick()

		requestAnimationFrame(() => {
			justOpened = false
		})

		window.addEventListener('pointerdown', handleOutsideClick)
		window.addEventListener('contextmenu', handleOutsideClick)
	})

	onDestroy(() => {
		window.removeEventListener('pointerdown', handleOutsideClick)
		window.removeEventListener('contextmenu', handleOutsideClick)
		if (closeSubMenuTimer) clearTimeout(closeSubMenuTimer)
	})
</script>

<div
	use:portal={resolvedPortalParent}
	class="context-menu"
	bind:this={menuDivElement}
	style="top: {adjustedY}px; left: {adjustedX}px;"
	transition:scaleFade={{ duration: 200, baseScale: 0.95 }}
>
	{#each items as item, index}
		{#if item.separator}
			<div class="separator"></div>
		{:else}
			{@const a = item as ContextMenuActionItem}
			<div
				role="none"
				class="item-wrapper {a.disabled ? 'disabled' : ''}"
				onmouseenter={(e) => handleItemMouseEnter(index, e.currentTarget as HTMLElement)}
				onmouseleave={handleItemMouseLeave}
			>
				<div class="item-inner">
					<button
						class="menu-item"
						class:text-danger={a.isNegative}
						disabled={a.disabled}
						onclick={(event) => handleClick(event, a.action, a.disabled)}
					>
						{#if a.icon}
							<span class="icon-badge {a.className ?? ''}">
								<Icon name={a.icon as unknown as IconName} size="xs" />
							</span>
						{/if}
						{a.label}
					</button>
					{#if a.subItems?.length}
						<div class="flex items-center pr-4">
							<Icon name="chevronRight" size="sm" class="text-[#002C4D80]" />
						</div>
					{:else if a.shortcut}
						<div class="key-item">{a.shortcut}</div>
					{/if}
				</div>
			</div>
		{/if}
	{/each}
</div>

{#if activeSubMenuIndex >= 0 && subMenuAnchorRect && activeSubMenuItems.length}
	<div
		role="none"
		use:portal={resolvedPortalParent}
		class="context-menu sub-context-menu"
		bind:this={subMenuDivElement}
		style="top: {adjustedSubMenuY}px; left: {adjustedSubMenuX}px;"
		onmouseenter={handleSubMenuMouseEnter}
		onmouseleave={handleSubMenuMouseLeave}
		transition:scaleFade={{ duration: 150, baseScale: 0.95 }}
	>
		{#each activeSubMenuItems as subItem}
			{#if subItem.separator}
				<div class="separator"></div>
			{:else}
				{@const s = subItem as ContextMenuActionItem}
				<div class="item-wrapper {s.disabled ? 'disabled' : ''}">
					<div class="item-inner">
						<button
							class="menu-item"
							class:text-danger={s.isNegative}
							disabled={s.disabled}
							onclick={(event) => handleClick(event, s.action, s.disabled)}
						>
							{#if s.icon}
								<span class="icon-badge {s.className ?? ''}">
									<Icon name={s.icon as unknown as IconName} size={s.iconSize ?? 'md'} />
								</span>
							{/if}
							{s.label}
						</button>
						{#if s.shortcut}
							<div class="key-item">{s.shortcut}</div>
						{/if}
					</div>
				</div>
			{/if}
		{/each}
	</div>
{/if}

<style lang="postcss">
	.context-menu {
		transition:
			top 0.2s ease-in-out,
			left 0.2s ease-in-out;
		@apply flex flex-col
			absolute z-[1000] w-[250px] px-px py-2 bg-white rounded-lg 
			shadow-[0px_20px_24px_-4px_rgba(0,0,0,0.08)]
			shadow-[0px_8px_8px_-4px_rgba(0,0,0,0.04)]
			outline outline-1 outline-offset-[-1px] outline-[#003a66]/10 
			inline-flex flex-col justify-start items-start overflow-hidden;
	}

	.sub-context-menu {
		transition: none;
	}

	.item-wrapper {
		@apply self-stretch inline-flex flex-col justify-center items-start;
	}
	.item-wrapper:not(.disabled):hover {
		@apply bg-[#004980]/5;
	}
	.item-wrapper.disabled {
		@apply cursor-not-allowed opacity-50;
	}

	.item-inner {
		@apply self-stretch inline-flex justify-between items-center gap-2 w-full;
	}

	.menu-item {
		@apply text-[rgba(0,22,38,0.9)] px-4 py-2 w-full text-base font-normal leading-6 text-left bg-transparent p-0 m-0 inline-flex items-center gap-2;
	}

	.icon-badge {
		@apply w-6 h-6 rounded-md inline-flex items-center justify-center flex-shrink-0;
	}

	.text-danger {
		color: red;
	}

	.key-item {
		@apply text-[rgba(0,44,77,0.5)] text-sm pr-4;
	}

	.chevron-item {
		@apply text-[rgba(0,44,77,0.5)] text-base pr-3 select-none;
	}

	.separator {
		@apply border-t border-soft-gray/50 my-2 mx-4 self-stretch;
	}
</style>
